// dev-desktop-nav — open/close behaviour for the desktop mega menus
// (blocks/dev-desktop-nav.liquid, blocks/dev-dnav-item.liquid).
//
// One delegated set of listeners on `document`, bound once, so the theme editor
// can add, remove and reorder items without a rescan, and a section re-render
// cannot stack duplicate handlers. Same pattern as dev-mobile-header.js.
//
//   mouse      hover opens after a short intent delay; leaving the item (label
//              AND its panel — the panel is a DOM child) closes after a grace
//              period, so a diagonal move toward a card does not drop the menu.
//   touch/pen  hover is ignored; the toggle button opens and closes.
//   keyboard   the toggle button opens and closes; Escape closes and returns
//              focus; tabbing out of an item closes it.
(() => {
  if (window.__devDesktopNavBound) return;
  window.__devDesktopNavBound = true;

  const ITEM = '[data-dev-dnav-mega]';
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 220;

  let openTimer = 0;
  let closeTimer = 0;

  const navOf = (item) => item.closest('[data-dev-dnav]');
  const toggleOf = (item) => item.querySelector('[data-dev-dnav-toggle]');
  const openItem = () => document.querySelector(`${ITEM}.is-open`);

  // Card images are lazy and the panels are zero-height until opened, so the
  // browser would only start fetching them on the first open — a visible pop.
  // The first sign of intent anywhere in the nav fetches them all.
  let warmed = false;
  function warm(nav) {
    if (warmed || !nav) return;
    warmed = true;
    nav.querySelectorAll('.dev-mega-card__img').forEach((img) => {
      img.loading = 'eager';
    });
  }

  function setOpen(item, open) {
    item.classList.toggle('is-open', open);
    const toggle = toggleOf(item);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function open(item) {
    clearTimeout(closeTimer);
    const current = openItem();
    if (current === item) return;
    const nav = navOf(item);
    warm(nav);
    if (current) {
      // Swap in place: no collapse/expand pair stacking two panels.
      nav.setAttribute('data-switching', '');
      setOpen(current, false);
      setOpen(item, true);
      requestAnimationFrame(() => requestAnimationFrame(() => nav.removeAttribute('data-switching')));
      return;
    }
    setOpen(item, true);
  }

  function close(item = openItem()) {
    clearTimeout(openTimer);
    if (item) setOpen(item, false);
  }

  const isDesktop = () => window.matchMedia('(min-width: 1200px)').matches;

  // ---- mouse hover ----
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !isDesktop()) return;
    const nav = event.target.closest('[data-dev-dnav]');
    if (nav) warm(nav);
    const item = event.target.closest(ITEM);
    if (!item) return;
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    // Already showing a panel: switch immediately, the customer is browsing.
    if (openItem()) open(item);
    else openTimer = setTimeout(() => open(item), OPEN_DELAY);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = event.target.closest(ITEM);
    if (!item || item.contains(event.relatedTarget)) return;
    clearTimeout(openTimer);
    if (item.classList.contains('is-open')) {
      closeTimer = setTimeout(() => close(item), CLOSE_DELAY);
    }
  });

  // ---- click / tap / keyboard activation ----
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-dev-dnav-toggle]');
    if (toggle) {
      const item = toggle.closest(ITEM);
      if (!item) return;
      event.preventDefault();
      if (item.classList.contains('is-open')) close(item);
      else open(item);
      return;
    }
    // A click anywhere outside the open item closes it.
    const current = openItem();
    if (current && !current.contains(event.target)) close(current);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const current = openItem();
    if (!current) return;
    close(current);
    const toggle = toggleOf(current);
    if (toggle) toggle.focus();
  });

  // Tabbing past the last card (or back out above the label) closes the panel,
  // so a keyboard user never leaves a menu hanging over the page.
  document.addEventListener('focusout', (event) => {
    const item = event.target.closest(ITEM);
    if (!item || !item.classList.contains('is-open')) return;
    if (event.relatedTarget && item.contains(event.relatedTarget)) return;
    if (!event.relatedTarget) return; // focus left the window, not the item
    close(item);
  });

  // Dropping below 990 hides this header entirely; do not leave it open.
  const wide = window.matchMedia('(min-width: 1200px)');
  const onChange = (e) => {
    if (!e.matches) close();
    else keepMenuStyle(document.querySelector('#header-component'));
  };
  if (wide.addEventListener) wide.addEventListener('change', onChange);
  else wide.addListener(onChange);

  // TOUCH SCREENS AT DESKTOP WIDTH. Horizon flips the header into its
  // hamburger-drawer layout on any touch device (utilities.js →
  // setHeaderMenuStyle: `isTouchDevice() ? 'drawer' : 'menu'`), even on an iPad
  // in landscape at 1180px. That layout hides the menu and re-grids the bar for
  // phones. This nav already works by tap — every mega item has a real toggle
  // button — so at >= 990 the Figma desktop bar is kept: whenever Horizon writes
  // 'drawer' there, it is put back to 'menu'. Horizon only writes the attribute
  // from its own resize/overflow events, so this cannot ping-pong.
  function keepMenuStyle(header) {
    if (!header || !header.querySelector('[data-dev-dnav]')) return;
    if (isDesktop() && header.dataset.menuStyle === 'drawer') header.dataset.menuStyle = 'menu';
  }

  function watchHeader() {
    const header = document.querySelector('#header-component');
    if (!header || header.__devDnavWatched) return;
    header.__devDnavWatched = true;
    keepMenuStyle(header);
    new MutationObserver(() => keepMenuStyle(header)).observe(header, {
      attributes: true,
      attributeFilter: ['data-menu-style'],
    });
  }

  watchHeader();
  document.addEventListener('shopify:section:load', watchHeader);

  // ---- adaptive scrolled header ----
  // When the bar floats over the page (desktop sticky / mobile bar), sample the
  // section directly beneath it and tell CSS whether that ground is dark or
  // light, so the frosted glass and ink follow the page instead of staying
  // off-white over a deep-green band. The nearest ancestor with an opaque
  // background colour IS the ground; images are skipped because their colour
  // cannot be read and the section behind them is what the design colours.
  // Only a BAND counts as ground: an ancestor at least 90% of the viewport
  // wide. A dark button, card or chip passing under the sample point used to
  // flip the bar to dark for a few pixels of scroll and back again.
  function groundUnder(header) {
    const r = header.getBoundingClientRect();
    const y = Math.min(window.innerHeight - 1, Math.max(0, r.bottom + 2));
    const minW = window.innerWidth * 0.9;
    const stack = document.elementsFromPoint(window.innerWidth / 2, y);
    for (const el of stack) {
      if (header.contains(el) || el.closest('#header-group, .dev-mheader, dialog')) continue;
      for (let n = el; n && n !== document.documentElement; n = n.parentElement) {
        if (n.getBoundingClientRect().width < minW) continue;
        const m = getComputedStyle(n).backgroundColor.match(/[\d.]+/g);
        if (!m) continue;
        const [r0, g0, b0, a = 1] = m.map(Number);
        if (a < 0.5) continue;
        const lum = (0.2126 * r0 + 0.7152 * g0 + 0.0722 * b0) / 255;
        return lum < 0.45 ? 'dark' : 'light';
      }
      break;
    }
    return 'light';
  }

  let groundRaf = 0;
  function updateGround() {
    groundRaf = 0;
    for (const header of document.querySelectorAll('#header-component, .dev-mheader')) {
      if (!header.offsetParent && getComputedStyle(header).position !== 'fixed') continue; // hidden at this width
      const g = groundUnder(header);
      if (header.dataset.devGround !== g) header.dataset.devGround = g;
    }
  }
  const scheduleGround = () => { if (!groundRaf) groundRaf = requestAnimationFrame(updateGround); };
  // capture: Horizon scrolls .page-wrapper on desktop, not the window
  document.addEventListener('scroll', scheduleGround, { capture: true, passive: true });
  window.addEventListener('resize', scheduleGround);
  window.addEventListener('load', scheduleGround);
  scheduleGround();

  // 869fc07kv: the bar goes sticky (Horizon flips data-sticky-state from its
  // IntersectionObserver) and paints the LIGHT frosted underlay for a frame or
  // two before the next scroll rAF reads the dark section under it — a
  // visible light flash on every dark page. Re-reading the ground in the same
  // microtask as the state change means the first sticky frame is already the
  // right colour.
  const groundMo = new MutationObserver(updateGround);
  const watchGround = () => {
    for (const header of document.querySelectorAll('#header-component')) {
      groundMo.observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });
    }
  };
  watchGround();
  document.addEventListener('shopify:section:load', watchGround);

  // Theme editor: selecting a card (or its item) in the sidebar opens that
  // panel so the merchant can see what they are editing; deselecting closes it.
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest && event.target.closest(ITEM);
    if (item) open(item);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const item = event.target.closest && event.target.closest(ITEM);
    if (item) close(item);
  });
})();
